import { useState, useCallback } from 'react';
import type { PostureAnalysis } from '@/lib/types';
import { createClientProspect, linkProspectPostureAnalysis } from '../client-prospects-api';
import type { ProspectContact } from '../prospect-contact';
import logger from '@/lib/logging';

interface UseSelfAssessmentProspectReturn {
  /** Create a prospect record and return the new prospect ID, or null on error. */
  createProspect: (
    contact: ProspectContact,
    gdprConsent: boolean,
    pictureConsent?: boolean,
  ) => Promise<string | null>;
  /** Link a posture analysis to an existing prospect. */
  linkAnalysis: (
    prospectId: string,
    analysis: PostureAnalysis,
    gdprConsent: boolean,
    savePictures?: boolean,
  ) => Promise<void>;
  isSubmitting: boolean;
  error: string | null;
}

/**
 * Shared hook that wraps the prospect creation and posture-analysis linking
 * workflow used by both self-assessment flows.
 *
 * Usage:
 *   const { createProspect, linkAnalysis, isSubmitting, error } = useSelfAssessmentProspect();
 *
 *   // On form submit:
 *   const id = await createProspect({ name, email }, gdprConsent);
 *   if (id && analysis) {
 *     await linkAnalysis(id, analysis, gdprConsent);
 *   }
 */
export function useSelfAssessmentProspect(): UseSelfAssessmentProspectReturn {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createProspect = useCallback(
    async (
      contact: ProspectContact,
      gdprConsent: boolean,
      pictureConsent = false,
    ): Promise<string | null> => {
      setIsSubmitting(true);
      setError(null);
      try {
        const id = await createClientProspect(contact, gdprConsent, pictureConsent);
        logger.info('[useSelfAssessmentProspect] Prospect created', { prospectId: id });
        return id;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to create prospect.';
        logger.error('[useSelfAssessmentProspect] createProspect error', { error: message });
        setError(message);
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [],
  );

  const linkAnalysis = useCallback(
    async (
      prospectId: string,
      analysis: PostureAnalysis,
      gdprConsent: boolean,
      savePictures = true,
    ): Promise<void> => {
      try {
        await linkProspectPostureAnalysis(prospectId, analysis, gdprConsent, savePictures);
        logger.info('[useSelfAssessmentProspect] Analysis linked', { prospectId });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to link analysis.';
        logger.error('[useSelfAssessmentProspect] linkAnalysis error', { error: message });
        // Non-fatal: prospect was already created — don't surface to user.
      }
    },
    [],
  );

  return { createProspect, linkAnalysis, isSubmitting, error };
}
